/**
 * Pure helpers for calendar event day-overlap math.
 *
 * Extracted from `app/(app)/events/page.tsx` so the filter logic is unit-
 * testable and shared between the calendar grid (which days have dots) and
 * the selected-day list (which events show when a day is tapped).
 *
 * Background: this fixes a regression where the web calendar view used a
 * same-day check on `event.start` only — multi-day events appeared on day 1
 * but not on subsequent days. iOS and Android have always used range overlap.
 * See [[tasks/zoey-59]] and [[gramata-bg-calendar-events-imported-but-not-showin]].
 */

/**
 * Returns true if an event overlaps the calendar day containing `date`.
 *
 * "Overlap" = event starts before the day ends AND ends after the day starts.
 * Correctly includes:
 *   - Single-day events whose start is on this day
 *   - Multi-day events that span this day (start was earlier and/or end is later)
 *   - All-day events on this day
 *
 * An event with no `start` is never considered overlapping.
 * An event with no `end` is treated as a point event at `start`.
 *
 * Zero- or negative-duration events (a point in time, or an all-day event
 * mistakenly stored with `end === start` at midnight — the shape older iOS and
 * Android all-day events were saved with) overlap the day that contains their
 * start instant. Without this, the strict `end > dayStart` check below excludes
 * a midnight point event from its own day.
 *
 * @param eventStart  The event's start date (or null/undefined).
 * @param eventEnd    The event's end date (or null/undefined).
 * @param date        Any moment within the day to test against.
 */
export function eventOverlapsDay(
  eventStart: Date | null | undefined,
  eventEnd: Date | null | undefined,
  date: Date,
): boolean {
  if (!eventStart) return false;
  const end = eventEnd ?? eventStart;
  const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);
  if (end <= eventStart) {
    // Point event: overlaps the day that contains `start`.
    return eventStart >= dayStart && eventStart < dayEnd;
  }
  // Half-open interval [start, end) vs half-open day [dayStart, dayEnd).
  return eventStart < dayEnd && end > dayStart;
}

/**
 * Returns true when an event should be treated as "all day". Mirrors the iOS
 * `CalendarEvents.isAllDay` / Android `CalendarEvent.isAllDay` semantics:
 *   - the explicit `all_day` flag wins, otherwise
 *   - both start and end sit exactly on local midnight AND differ (the
 *     exclusive-end shape a well-formed all-day event is stored with).
 *
 * Note: a legacy zero-duration event saved with `start === end` at midnight is
 * NOT detected here (start must differ from end) — matching iOS/Android. Such
 * events need a re-save to gain the explicit flag / exclusive end.
 */
export function isAllDayEvent(
  allDay: boolean | null | undefined,
  start: Date | null | undefined,
  end: Date | null | undefined,
): boolean {
  if (allDay === true) return true;
  if (!start || !end) return false;
  if (start.getTime() === end.getTime()) return false;
  return (
    start.getHours() === 0 &&
    start.getMinutes() === 0 &&
    end.getHours() === 0 &&
    end.getMinutes() === 0
  );
}

/**
 * Returns true when two dates fall on the same calendar day in local time.
 * Used for "is today" and "is selected" grid highlighting (NOT for event
 * filtering — use `eventOverlapsDay` for that, which handles multi-day spans).
 */
export function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Returns the canonical "year-month-day" key used by the calendar grid Set
 * to mark days that contain events. The key is timezone-local (matches the
 * calendar grid's local-time rendering).
 */
export function dayKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

/**
 * Returns the set of `dayKey` strings for every day a single event spans
 * (inclusive of both start and end days). Used by the calendar grid to
 * stamp dots on every day a multi-day event covers.
 *
 * If `eventStart` is null/undefined, returns an empty set.
 * If `eventEnd` is missing, the event is treated as a single-day point event.
 *
 * Iteration is bounded to a sane maximum (366 days) to defend against
 * malformed events with absurd end dates.
 */
export function dayKeysForEvent(
  eventStart: Date | null | undefined,
  eventEnd: Date | null | undefined,
): Set<string> {
  const keys = new Set<string>();
  if (!eventStart) return keys;
  const end = eventEnd ?? eventStart;
  // Walk day-by-day from start to end (inclusive). Use local-day arithmetic
  // so DST transitions don't double-count or skip a day.
  let cursor = new Date(
    eventStart.getFullYear(),
    eventStart.getMonth(),
    eventStart.getDate(),
  );
  const lastDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  let safety = 0;
  while (cursor <= lastDay && safety < 366) {
    keys.add(dayKey(cursor));
    cursor = new Date(
      cursor.getFullYear(),
      cursor.getMonth(),
      cursor.getDate() + 1,
    );
    safety += 1;
  }
  return keys;
}
